import { createApp, createSSRApp } from 'vue'
import { loadLanguageAsync } from 'laravel-vue-i18n'

/**
 * The page's Vue app. When the SSR server rendered the page (patient site), the mount element already
 * holds that markup and the app has to hydrate it rather than throw it away and paint it again; when
 * it did not (a portal page, or the SSR server was down and Laravel fell back), the element is empty
 * and the app renders from scratch as before.
 */
export function createPageApp(el, rootComponent) {
  if (!el?.hasChildNodes()) return createApp(rootComponent)

  moveBodyTeleportsToFront(el)

  return createSSRApp(rootComponent)
}

/**
 * Vue hydrates what a page teleports to <body> (modals, popups) starting at the first node of <body>.
 * The SSR server's teleported markup arrives right after the app's element (ssr.js), behind the root
 * view's <noscript> and whitespace, so Vue met the <noscript> where it expected the teleport and logged
 * "Hydration completed but contains mismatches" on every page with a teleport (Novo landing, Novo
 * instant appointment, /consult). Moving those nodes to the front of <body> before hydrating puts them
 * where Vue looks; they are fixed-position overlays or empty placeholders, so nothing moves on screen.
 */
function moveBodyTeleportsToFront(el) {
  const isComment = (node, text) => node.nodeType === Node.COMMENT_NODE && node.data === text
  const nodes = []
  let depth = 0
  for (let node = el.nextSibling; node; ) {
    const starts = isComment(node, 'teleport start anchor')
    if (!starts && depth === 0) break
    if (starts) depth++
    else if (isComment(node, 'teleport anchor')) depth--
    const next = node.nextSibling
    nodes.push(node)
    node = next
  }
  if (!nodes.length || el.parentNode !== document.body) return

  const block = document.createDocumentFragment()
  nodes.forEach((node) => block.appendChild(node))
  document.body.insertBefore(block, document.body.firstChild)
}

/**
 * The language the server rendered this page in: HandleInertiaRequests shares it as current_locale, and
 * the root view writes the same into <html lang>.
 */
export function pageLanguage(props) {
  return props?.initialPage?.props?.current_locale || document.documentElement.lang || 'en'
}

/**
 * Mount the app once the page's language file is in. The language JSON is imported lazily, and mounting
 * straight away painted raw keys (auth.login.title) until it arrived. The other language stays lazy;
 * a failed load still mounts, with the keys, rather than leaving a blank page. A server-rendered page
 * stays on screen, already translated, while the file loads, and is hydrated with the same text.
 */
export function mountWhenTranslated(app, el, lang) {
  return loadLanguageAsync(lang)
    .catch(() => {})
    .finally(() => app.mount(el))
}
